<template>
  <div data-testid="filestack-container" @click.prevent="onClickPick">
    <slot></slot>
  </div>
</template>

<script>
import * as filestack from 'filestack-js';

export default {
  name: 'Filestack',
  props: {
    apikey: {
      type: String,
      default: ''
    },
    file: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'pick'
    },
    options: {
      type: Object,
      default: function () {
        return {};
      }
    },
    security: {
      type: Object,
      default: null
    },
    cname: {
      type: String,
      default: null
    },
    refreshSecurity: {
      type: Function,
      default: null
    },
    invalidateSecurity: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      client: null,
      picker: null
    };
  },
  async created() {
    if (this.options.displayMode === 'inline') {
      try {
        const security = await this.resolveSecurityForOpen();
        const result = await this.initClient(this.mode, this.apikey, this.options, this.file, security, this.cname);
        this.onSuccess(result);
      } catch (error) {
        this.onError(error);
      }
    }
  },
  methods: {
    onSuccess(result) {
      this.$emit('success', result);
    },
    onError(err) {
      this.$emit('error', err);
    },
    async resolveSecurityForOpen() {
      let security = this.security;
      if (this.refreshSecurity) {
        try {
          security = (await this.refreshSecurity()) || security;
        } catch (e) {
          // fall back to whatever security prop was already bound; initClient will
          // surface an auth failure to the caller if it's expired
        }
      }
      return security;
    },
    async onClickPick() {
      try {
        const security = await this.resolveSecurityForOpen();
        const result = await this.initClient(this.mode, this.apikey, this.options, this.file, security, this.cname);
        this.onSuccess(result);
      } catch (error) {
        this.onError(error);
      }
    },
    initClient(mode, apikey, options, file, security, cname) {
      this.$emit('init');
      const url = options.url;
      const handle = options.handle;
      const clientOptions = {};
      if (security) {
        clientOptions.security = security;
      }
      if (cname) {
        clientOptions.cname = cname;
      }
      this.client = filestack.init(apikey, clientOptions);
      const ops = Object.assign({}, options);
      delete ops.handle;
      delete ops.url;
      const that = this;
      this.client.on('upload.error', filestackError => {
        if (filestackError?.type == 'aborted') {
          this.picker.close();
          return;
        }
        this.invalidateSecurity?.(filestackError);
        that.$emit('error', filestackError);
        this.picker.close();
      });
      if (mode === 'transform') {
        return new Promise((resolve, reject) => {
          try {
            resolve(this.client.transform(url, ops));
          } catch (err) {
            reject(err);
          }
        });
      }
      if (mode === 'retrieve') {
        return this.client.retrieve(handle, ops);
      }
      if (mode === 'metadata') {
        return this.client.metadata(handle, ops);
      }
      if (mode === 'storeUrl') {
        return this.client.storeURL(url, ops);
      }
      if (mode === 'upload') {
        return this.client.upload(file, ops, undefined, undefined, security);
      }
      if (mode === 'remove') {
        return this.client.remove(handle, security);
      }
      this.picker = this.client.picker(ops);
      return this.picker.open();
    }
  }
};
</script>
